import { type PropsWithChildren, type ReactNode } from "react";
import { Collapse, CollapseContent, CollapseDetails, CollapseTrigger } from "@/components/ui/collapse.tsx";
import useFilterCollapseInitialOpen from "@/features/sub-families/hooks/use-filter-collapse-initial-open.ts";
import useFilterOptionScroll from "@/features/sub-families/hooks/use-filter-option-scroll.ts";
import useScrollToRef from "@/features/sub-families/hooks/use-scroll-to-ref.ts";
import useMergeRefs from "@/hooks/use-merge-refs.ts";

type Props = PropsWithChildren<{
    productProperty: string;
    title: ReactNode;
    details?: ReactNode;
    isDesign?: boolean;
    idx?: number;
    defaultOpenWhenNoProperty?: boolean;
    className?: string;
}>;

export default function FilterCollapse({
    productProperty,
    title,
    details,
    children,
    isDesign,
    idx,
    defaultOpenWhenNoProperty,
    className,
}: Props) {
    const scrollRef = useFilterOptionScroll<HTMLButtonElement>(productProperty);
    const scroll = useScrollToRef<HTMLButtonElement>();

    useMergeRefs(scrollRef, scroll.ref);

    const initialOpen = useFilterCollapseInitialOpen({
        productProperty,
        isDesign,
        idx,
        defaultOpenWhenNoProperty,
    });

    const onOpenChange = (open: boolean) => {
        if (!open) return;

        scroll.scrollToElement();
    };

    return (
        <Collapse initialOpen={initialOpen} onOpenChange={onOpenChange} className={className}>
            <CollapseTrigger ref={scrollRef}>
                {title}
                {!!details && <CollapseDetails>{details}</CollapseDetails>}
            </CollapseTrigger>
            <CollapseContent>{children}</CollapseContent>
        </Collapse>
    );
}
